import {makeAutoObservable, observable} from 'mobx'
import {Lang, Language} from 'yougile-lang'
import {FilenameUtil, logger, Util} from 'yougile-util'
import {DataObjectId, InitResp, Sandbox, User} from 'yougile-model'
import {initApi} from '@api/browser-console-api'
import {Accounts} from '@client/accounts'
import {backgroundImages} from '@client/background-images'
import {ClientLogger} from '@client/client-logger'
import {ClientUtilities} from '@client/client-utilities'
import {DesktopRenderer} from '@client/desktop-renderer'
import {EventBus} from '@client/event-bus'
import {
  backFromChatEvent,
  onBeforeBrowserUnloadEvent,
  openChatEvent,
  OpenChatEventData,
  showMessage
} from '@client/events'
import {NetworkStatusClient} from '@client/network-status-client'
import {PaymentClient} from '@client/payment-client'
import {PermissionsClient} from '@client/permissions-client'
import {Refresh} from '@client/refresh'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {sharedBoardsEvents} from '@client/shared-boards-events'
import {Sync, SyncAutoSaver} from '@client/sync'
import {WebPush} from '@client/web-push'
import {WebStorage} from '@client/web-storage'
import {Backend} from '@common/backend'
import {DragData} from '@common/drag-data'
import {Notifications} from '@common/notifications'
import {Tracking} from '@common/tracking'
import {UserCardClient} from '@common/user-card-client'
import {BoardTabBg} from '@components/board/board-tab-bg'
import {FileUploadProgress} from '@components/file-upload-progress'
import {MessageBox} from '@components/message-box'
import {showShareFail} from '@components/share-fail'
import {createUseStore} from '@containers/hooks'
import {Board} from '@extensions/board'
import {ChatUtils} from '@extensions/chat-utils'
import {Column} from '@extensions/column'
import {EmojiUtils} from '@extensions/emoji-utils'
import {globals} from '@extensions/globals-base'
import {MailboxManager} from '@extensions/mailbox'
import {MessageNotifier} from '@extensions/message-notifier'
import {NotificationSettingsService} from '@extensions/notifications-settings-service'
import {isLoggedInPage, Page} from '@extensions/page'
import {PageService} from '@extensions/page-service'
import {Project} from '@extensions/project'
import {TaskChatProxyService} from '@extensions/proxies/task-chat-proxy'
import {SubtaskUtil} from '@extensions/subtask-util'
import {SystemEntities} from '@extensions/system-entities'
import {ThemeUtils} from '@extensions/theme-utils'
import {MessageBoxType} from '../../types/message-box'

interface State {
  loaded: boolean
  page: Page
  loggedIn: boolean
  shareFail: boolean
}

export class RootStore {
  state: State

  constructor() {
    this.state = {
      loaded: false,
      page: 'loading',
      loggedIn: false,
      shareFail: false
    }

    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )

    this.prepareApp()
    this.init()
  }

  private onOpenChatEvent(data: OpenChatEventData): void {
    ChatUtils.openChat(ChatUtils.findChatForCurrentUser(data.chatId), {
      messageTime: data.messageTime,
      tab: data.tab,
      autofocus: data.autofocus
    })
  }

  private onBackFromChatEvent(): void {
    PageService.setHash('')
  }

  private onShowMessage({
    message,
    status
  }: {
    message: string
    status: MessageBoxType
  }): void {
    MessageBox.show(message, status)
  }

  private initEventListeners(): void {
    EventBus.on(openChatEvent, this.onOpenChatEvent)
    EventBus.on(backFromChatEvent, this.onBackFromChatEvent)
    EventBus.on(showMessage, this.onShowMessage)
  }

  private onLocationUpdate(): void {
    this.state.page = PageService.getPage()
    this.state.loggedIn = this.state.loaded && isLoggedInPage(this.state.page)
    if (this.state.page === 'api-editor') {
      globals.scriptsOpen = true
      globals.scriptsOpenUpdated?.()
    }
  }

  private async runAfterInit(): Promise<void> {
    if (this.state.shareFail) {
      globals.theme = 'light'
      const langLoc = Backend.loadLocal('lang') as Language
      if (langLoc) {
        Lang.setLang(langLoc)
      } else {
        ClientUtilities.setLangFromUrlOrBrowser()
      }
      this.state.loaded = true
      showShareFail()
      return
    }
    const userId = Backend.loadId()
    const userKey = Backend.loadKey()

    void Backend.initTimeUpdates()

    PageService.addLocationListener(this.onLocationUpdate, true)

    if (!globals.currentUserId && this.state.loaded && !Util.isDesktop()) {
      supportForm.add()
    }

    FileUploadProgress.init()
    MessageBox.init()
    MessageBox.initModern()
    UserCardClient.init()
    EmojiUtils.init()
    const langLoc = Backend.loadLocal('lang') as Language
    if (langLoc) {
      Lang.setLang(langLoc)
    } else {
      ClientUtilities.setLangFromUrlOrBrowser()
    }
    if (globals.lang) {
      Lang.setLang(globals.lang)
    }

    if (userId && userKey && this.state.page === 'login') {
      this.onAfterLogin(userId, userKey)
    } else {
      this.state.loaded = true
      this.state.page = PageService.getPage()
      supportForm.add()
    }
    void Tracking.sendUserProperty(
      {
        lang: Lang.getLang(),
        theme: globals.theme,
        userId: userId
      },
      true
    )
    Tracking.identify(userId, {
      lang: Lang.getLang(),
      theme: globals.theme,
      userId: userId
    })

    ThemeUtils.trackThemeVariation(undefined, true)

    this.initEventListeners()
  }

  private onAfterLogin(userId: DataObjectId, userKey: string): void {
    if (userKey.split('').pop() === '_' || !userKey.includes('_')) {
      globals.silentMode = true
    }

    if (!userKey.includes('_')) {
      globals.readonly = true
    }

    const onReload = (): void => {
      MessageNotifier.init(Notifications.notify, Notifications.ring)
      if (globals.currentUserId) {
        Accounts.createAccountInEveryCompany()
        BoardTabBg.init()
        Backend.saveLocal('lang', Lang.getLang())
      }
      this.state.loaded = true
      this.state.page = PageService.getPage()
    }
    const onBeforeReload = (): void => {
      this.state.loaded = false
      this.state.page = PageService.getPage()
      Project.clean()
      Board.clean()
      Column.clean()
      SystemEntities.clean()
    }
    Util.wait(10)
      .then(() =>
        Sync.init({
          userId,
          key: userKey,
          callback: () => this.onInit(userId),
          onReload,
          onBeforeReload,
          cacheService: {storage: WebStorage}
        })
      )
      .catch(error => logger.warn(error))
  }

  private onInit(userId: DataObjectId): void {
    globals.currentUserId = userId
    this.state.loaded = true
    this.state.page = PageService.getPage()
    NetworkStatusClient.init()
    WebPush.init(userId)

    NotificationSettingsService.migrateNotificationSettings()

    if (Util.isDesktop()) {
      DesktopRenderer.trackUsage()
    }
    MailboxManager.init()
    TaskChatProxyService.initCache()

    if (SharedBoardsClient.getId()) {
      const user = Sync.getProfile().get(globals.currentUserId) as User
      user.theme = SharedBoardsClient.settings.theme || 'light'
      user.skin = SharedBoardsClient.settings.background || backgroundImages[8]
      user.language = SharedBoardsClient.settings.lang || 'ru'
      globals.lang = user.language
      Lang.setLang(user.language)
    }
  }

  prepareApp(): void {
    if (!globals.standalone) {
      globals.standalone = 'standaloneMode' in window
    }
    Tracking.initialize()
    Tracking.enable()

    /**
     * Prevent Mac OS history navigation on x-wheel event.
     */
    document.addEventListener('wheel', (e: Event) => {
      const target: HTMLElement = e.target as HTMLElement
      if (target?.classList.contains('ps')) {
        e.preventDefault()
      }
    })

    window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
      const results = EventBus.dispatch(onBeforeBrowserUnloadEvent())
      const preventUnload = results.some(result => !!result)
      if (preventUnload) {
        event.preventDefault()
        return Lang.msg.editorCloseConfirm
      }
      return undefined
    })
  }

  private init(): void {
    const initAll = (): void => {
      ClientLogger.init()
      PermissionsClient.init()
      PaymentClient.init()
      initApi()
      SyncAutoSaver.init()
      this.state.loaded = false
      void this.startApp()
    }
    if (SharedBoardsClient.getId()) {
      void SharedBoardsClient.loadCredentials()
        .catch(() => false)
        .then(ok => {
          if (!ok) {
            this.state.shareFail = true
          }
          void sharedBoardsEvents.trigger()
          initAll()
        })
    } else {
      initAll()
    }
  }

  private async startApp(): Promise<void> {
    Sandbox.init({getParentTaskIds: id => SubtaskUtil.getParentTaskIds(id)})
    PageService.init()
    DragData.init()

    if (Util.isDesktop()) {
      DesktopRenderer.init()
    } else {
      Refresh.init(
        () => location.reload(),
        async (needRefresh = true) => {
          const key = Backend.loadKey()
          Backend.clearCredentials()
          await Sync.invalidateCache()
          sessionStorage.clear()
          await Backend.logout(key)
          logger.log('Refresh init: reload')
          needRefresh && Refresh.refresh()
        }
      )
    }

    const processInit = (res: InitResp): void => {
      FilenameUtil.dataUrl = res.dataUrl
      globals.noUsers = res.noUsers
      globals.noLicense = res.noLicense
      globals.licenseExpired = res.licenseExpired
      globals.allowExtensions = !!res.allowExtensions

      globals.mainCompanyId = res.mainCompanyId
      globals.allowWebExtensions = res.allowWebExtensions
      globals.webPlatformUrl = res.webPlatformUrl
      globals.supportSyncRoleUsers = res.supportSyncRoleUsers

      if (res.lang && !SharedBoardsClient.getId()) {
        globals.lang = res.lang
        Lang.setLang(res.lang)
      }
      if (res.disableEmailRecovery) {
        globals.disableEmailRecovery = true
      }
      if (res.disableInviteUsers) {
        globals.disableInviteUsers = true
      }
      void this.runAfterInit()
    }

    let attempts = 0
    const doBackendInit = (): void => {
      Backend.init()
        .then(res => {
          processInit(res)
          if (res) {
            const key = 'y-init::' + Backend.toMainUrl('')
            Backend.saveLocal(key, JSON.stringify(res))
          }
        })
        .catch(e => {
          logger.error(e)
          if (!Util.isDesktop()) {
            return
          }
          const key = 'y-init::' + Backend.toMainUrl('')
          const resStr = Backend.loadLocal(key)
          if (resStr) {
            try {
              const res = JSON.parse(resStr) as InitResp
              if (res) {
                globals.offline = true
                Backend.processInitResp(res)
                processInit(res)
                return
              }
            } catch (e) {
              logger.error(e)
            }
          }
          attempts++
          logger.log('Trying to connect again…')
          setTimeout(doBackendInit, Math.min(10000, 1000 + attempts * 500))
        })
    }

    doBackendInit()
  }
}

export const [RootContext, useRootStore] = createUseStore(RootStore)
export const rootStore = new RootStore()

const supportForm = {
  added: false,
  add(): void {
    if (Util.isDesktop()) {
      return
    }
    if (globals.standalone || supportForm.added) {
      return
    }
    /** Adding RedHelper offline form */
    const s = document.createElement('script')
    s.src = 'https://web.redhelper.ru/service/main.js?c=yougile'
    s.id = 'rhlpscrtg'
    s.async = true
    document.body.appendChild(s)
    supportForm.added = true
  }
}
